import { jsx, jsxs } from "react/jsx-runtime";
import {
  normalizePositionByRangeLength,
  getSequenceWithinRange,
  getOverlapsOfPotentiallyCircularRanges
} from "@teselagen/range-utils";
import {
  map,
  camelCase,
  startCase,
  startsWith,
  flatMap,
  assign,
  noop
} from "lodash-es";
import {
  getComplementSequenceString,
  defaultEnzymesByName
} from "@teselagen/sequence-utils";
import React, { useMemo } from "react";
import pluralize from "pluralize";
import SelectionLayer from "./SelectionLayer";
import Sequence from "./Sequence";
import Axis from "./Axis";
import Orfs from "./Orfs";
import Translations from "./Translations";
import Labels from "./Labels";
import Cutsites from "./Cutsites";
import Caret from "./Caret";
import StackedAnnotations from "./StackedAnnotations";
import "./style.css";
import Chromatogram from "./Chromatograms/Chromatogram";
import { rowHeights } from "../RowView/estimateRowHeight";
import { getAllSelectionLayers } from "../utils/selectionLayer";
import { filter } from "lodash-es";
import { defaultCharWidth } from "../constants/rowviewContants";
import { CutsiteSelectionLayers } from "./CutsiteSelectionLayers";
import { view } from "@risingstack/react-easy-state";
import { featureDragStore } from "./StackedAnnotations/featureDragUtils";
function filterRanges(ranges, isProteinAlignmentView, extraProps = {}) {
  if (!ranges) return ranges;
  if (extraProps.onlyForward) {
    ranges = filter(ranges, (a) => a.annotation.forward);
  }
  if (extraProps.onlyReverse) {
    ranges = filter(ranges, (a) => !a.annotation.forward);
  }
  if (isProteinAlignmentView) {
    ranges = map(ranges, (range) => {
      const start = (range.start + 3) / 3 - 1;
      const end = (range.end + 1) / 3 - 1;
      return {
        ...range,
        start,
        end,
        annotation: {
          ...range.annotation,
          start,
          end
        }
      };
    });
  }
  return ranges;
}
function getPropsForType(props, isProteinAlignmentView, type, pluralType, extraProps) {
  const upperPluralType = startCase(pluralType);
  let labelData = props.row[pluralType];
  if (type === "feature") {
    labelData = filter(labelData, (f) => f.annotation.type !== "restriction_site");
  }
  const annotationRanges = filterRanges(
    labelData,
    isProteinAlignmentView,
    extraProps
  );
  const toRet = {
    annotationColor: props[pluralType + "Color"],
    annotationHeight: props[pluralType + "Height"] || rowHeights[pluralType].height,
    spaceBetweenAnnotations: props["spaceBetweenAnnotations" + upperPluralType] || rowHeights[pluralType].spaceBetweenAnnotations,
    marginTop: props[pluralType + "MarginTop"] || rowHeights[pluralType].marginTop,
    marginBottom: props[pluralType + "MarginBottom"] || rowHeights[pluralType].marginBottom,
    annotationRanges,
    showLabels: props.annotationLabelVisibility && props.annotationLabelVisibility[pluralType],
    onClick: props[type + "Clicked"],
    onRightClick: props[type + "RightClicked"],
    onDoubleClick: props[type + "DoubleClicked"]
  };
  return toRet;
}
export default function RowItem(props) {
  let {
    noRedux,
    charWidth = defaultCharWidth,
    selectionLayer = { start: -1, end: -1 },
    deletionLayers = {},
    replacementLayers = {},
    searchLayers = [],
    rowTopComp,
    rowBottomComp,
    isProtein,
    isRna,
    tickSpacing,
    truncateLabelsThatDoNotFit = true,
    aminoAcidNumbersHeight = rowHeights.aminoAcidNumbers.height,
    cutsiteLabelHeight = rowHeights.cutsiteLabels.height,
    sequenceHeight = rowHeights.sequence.height,
    axisHeight = rowHeights.axis.height,
    primerHeight = rowHeights.primers.height,
    axisMarginTop = rowHeights.axis.marginTop,
    width,
    annotationVisibility = {},
    annotationLabelVisibility = {},
    additionalSelectionLayers = [],
    caretPosition = -1,
    row = {
      sequence: "",
      start: 0,
      end: 0,
      rowNumber: 0
    },
    readOnly,
    isRowView,
    emptyText,
    alignmentType,
    alignmentData,
    sequenceLength = row.sequence.length,
    chromatogramData,
    fullSequence = "",
    replacementLayerClicked = noop,
    replacementLayerRightClicked = noop,
    searchLayerClicked = noop,
    backgroundRightClicked = noop,
    selectionLayerRightClicked = noop,
    searchLayerRightClicked = noop,
    translationDoubleClicked = noop,
    minHeight = 22,
    bpsPerRow = sequenceLength,
    editorName,
    rowContainerStyle,
    onScroll,
    scrollData,
    onlyShowLabelsThatDoNotFit,
    labelLineIntensity,
    isLinearView,
    scalePct,
    setScalePct,
    extraAnnotationProps = {},
    showAminoAcidUnitAsCodon
  } = props;
  const {
    chromatogram: showChromatogram,
    // orfLabels: showOrfLabel,
    cutsites: showCutsites,
    cutsitesInSequence: showCutsitesInSequence,
    axis: showAxis,
    axisNumbers: showAxisNumbers,
    // yellowAxis: showYellowAxis,
    aminoAcidNumbers: showAminoAcidNumbers,
    dnaColors: showDnaColors,
    serineThreonine: showSerineThreonine,
    hydrophobicity: showHydrophobicity,
    polar: showPolar,
    negative: showNegative,
    positive: showPositive,
    charged: showCharged,
    aliphatic: showAliphatic,
    aromatic: showAromatic,
    colorScheme: showColorScheme,
    fivePrimeThreePrimeHints,
    reverseSequence: showReverseSequence,
    sequence: showSequence,
    height
  } = annotationVisibility;
  const { sequence = "", cutsites = [] } = row;
  const reverseSequence = showReverseSequence ? getComplementSequenceString(
    alignmentData && alignmentData.sequence || sequence,
    isRna
  ) : "";
  const getGaps = useMemo(() => {
    if (alignmentData) {
      const gapMap = getGapMap(alignmentData.sequence);
      return (rangeOrCaretPosition) => {
        if (typeof rangeOrCaretPosition !== "object") {
          return {
            gapsBefore: gapMap[Math.min(rangeOrCaretPosition, gapMap.length - 1)]
          };
        }
        const { start, end } = rangeOrCaretPosition;
        const toReturn = {
          gapsBefore: gapMap[start],
          gapsInside: gapMap[Math.min(end, gapMap.length - 1)] - gapMap[Math.min(start, gapMap.length - 1)]
        };
        return toReturn;
      };
    } else {
      return getGapsDefault;
    }
  }, [alignmentData]);
  if (!row) {
    return null;
  }
  if (!width || isNaN(width)) {
    width = bpsPerRow * charWidth;
  }
  if (isLinearView) {
    width -= (charWidth || 12) * bpsPerRow.toString().length / 2;
  }
  charWidth = charWidth || width / Math.max(bpsPerRow, 1);
  if (isNaN(charWidth)) charWidth = 12;
  if (isNaN(width)) width = 0;
  const rowStyle = {
    position: "relative",
    minHeight,
    height,
    width: width + "px"
  };
  const isProteinAlignmentView = !!(props.isProtein && props.alignmentData);
  const annotationCommonProps = {
    noRedux,
    editorName,
    charWidth,
    bpsPerRow,
    getGaps,
    isProtein: isProteinAlignmentView ? false : isProtein,
    readOnly,
    sequenceLength,
    isRowView,
    row: {
      start: row.start,
      end: isProteinAlignmentView ? sequenceLength - 1 : row.end
    }
  };
  const drawLabels = (type, noDraw, { filterOpts, ...extraProps } = {}) => {
    if (noDraw) {
      return null;
    }
    const pluralType = pluralize(type);
    const isVisible = annotationVisibility[pluralType] && annotationLabelVisibility[pluralType];
    if (!isVisible) return null;
    let labelData = row[pluralType];
    if (type === "feature") {
      labelData = filter(labelData, (f) => f.annotation.type !== "restriction_site");
    }
    const ranges = filterRanges(
      labelData.length > 0 ? map(labelData, (a) => {
        return assign(a, {
          onClick: props[type + "Clicked"],
          onRightClick: props[type + "RightClicked"],
          onDoubleClick: props[type + "DoubleClicked"]
        });
      }) : [],
      isProteinAlignmentView,
      filterOpts
    );
    if (!ranges.length) return null;
    return /* @__PURE__ */ jsx(
      Labels,
      {
        ...annotationCommonProps,
        onlyShowLabelsThatDoNotFit,
        labelLineIntensity,
        rangeMax: bpsPerRow,
        annotationRanges: ranges,
        annotationHeight: cutsiteLabelHeight,
        ...extraProps
      }
    );
  };
  const drawAnnotations = (type, extraProps = {}) => {
    const {
      CompOverride,
      shouldShow,
      noPlural,
      alignmentType: alignmentType2,
      onlyForward,
      onlyReverse,
      ...otherExtraProps
    } = extraProps;
    const pluralType = noPlural ? type : pluralize(type);
    if (shouldShow !== void 0 ? !shouldShow : !annotationVisibility[pluralType] || Object.keys(row[pluralType] || {}).length <= 0) {
      return null;
    }
    const propsForType = getPropsForType(
      props,
      isProteinAlignmentView,
      type,
      pluralType,
      extraProps
    );
    const CompToUse = CompOverride || StackedAnnotations;
    return /* @__PURE__ */ jsx(
      CompToUse,
      {
        truncateLabelsThatDoNotFit,
        onlyShowLabelsThatDoNotFit,
        type,
        fullSequence,
        containerClassName: camelCase("veRowView-" + pluralType + "Container"),
        alignmentType: alignmentType2,
        upsertFeature: props.upsertFeature,
        ...annotationCommonProps,
        ...propsForType,
        ...otherExtraProps
      }
    );
  };
  const deletionLayersToDisplay = flatMap(
    { ...replacementLayers, ...deletionLayers },
    function(layer) {
      if (layer.caretPosition > -1) {
        return [];
      }
      const overlaps = getOverlapsOfPotentiallyCircularRanges(
        layer,
        row,
        sequenceLength
      );
      return overlaps;
    }
  );
  const deletionLayerStrikeThrough = deletionLayersToDisplay.length ? deletionLayersToDisplay.map(function(layer, index) {
    const left = (layer.start - row.start) * charWidth;
    const width2 = (layer.end - layer.start + 1) * charWidth;
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: "ve_sequence_strikethrough",
        style: {
          left,
          width: width2,
          top: 10,
          height: 2,
          position: "absolute",
          background: "black"
        }
      },
      "deletionLayer" + index
    );
  }) : null;
  const translationCommonProps = {
    CompOverride: Translations,
    showAminoAcidNumbers,
    showAminoAcidUnitAsCodon,
    sequenceLength,
    aminoAcidNumbersHeight
  };
  const partProps = {
    getExtraInnerCompProps: function(annotationRange) {
      const { annotation } = annotationRange;
      const { color } = annotation;
      const colorToUse = startsWith(color, "override_") ? color.replace("override_", "") : "#ac68cc";
      let extraProps = {};
      if (typeof extraAnnotationProps["part"] === "function") {
        extraProps = extraAnnotationProps["part"](annotation);
      }
      return {
        textColor: colorToUse,
        stroke: colorToUse,
        ...extraProps
      };
    },
    alignmentType
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: rowContainerStyle,
      onContextMenu: backgroundRightClicked,
      onScroll,
      className: "veRowItemWrapper",
      children: [
        rowTopComp && rowTopComp,
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "veRowItem",
            "data-row-start": row.start,
            "data-row-end": row.end,
            "data-row-number": row.rowNumber,
            style: rowStyle,
            children: [
              /* @__PURE__ */ jsx(
                SelectionLayer,
                {
                  className: "veSearchLayerContainer",
                  customTitleStart: "Search match",
                  regions: searchLayers,
                  ...annotationCommonProps,
                  showAminoAcidUnitAsCodon,
                  selectionLayerRightClicked: searchLayerRightClicked,
                  row: alignmentData ? { start: 0, end: alignmentData.sequence.length - 1 } : row,
                  onClick: searchLayerClicked
                }
              ),
              /* @__PURE__ */ jsx(
                SelectionLayer,
                {
                  isDraggable: true,
                  selectionLayerRightClicked,
                  ...annotationCommonProps,
                  row: alignmentData ? { start: 0, end: alignmentData.sequence.length - 1 } : row,
                  regions: getAllSelectionLayers({
                    additionalSelectionLayers,
                    selectionLayer
                  }),
                  showAminoAcidUnitAsCodon
                }
              ),
              drawAnnotations("warning"),
              drawAnnotations("assemblyPiece"),
              drawAnnotations("lineageAnnotation"),
              drawLabels("part"),
              drawAnnotations("part", partProps),
              drawAnnotations("orf", {
                CompOverride: Orfs
              }),
              charWidth > 4 && drawAnnotations("translation", {
                ...translationCommonProps,
                onDoubleClick: translationDoubleClicked
              }),
              showChromatogram && chromatogramData && /* @__PURE__ */ jsx(
                Chromatogram,
                {
                  chromatogramData,
                  alignmentData,
                  scalePct,
                  setScalePct,
                  ...annotationCommonProps
                }
              ),
              drawLabels("cutsite", !isRowView),
              drawLabels("primer", false, {
                filterOpts: { onlyForward: true }
              }),
              drawAnnotations("primer", {
                sequence: fullSequence,
                annotationHeight: primerHeight,
                onlyForward: true
              }),
              /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "veRowItemSequenceContainer",
                  style: { position: "relative" },
                  children: [
                    showSequence && /* @__PURE__ */ jsxs(
                      Sequence,
                      {
                        sequenceLength,
                        cutsites,
                        showDnaColors,
                        showSerineThreonine,
                        showHydrophobicity,
                        showPolar,
                        showNegative,
                        showPositive,
                        showCharged,
                        showAliphatic,
                        showAromatic,
                        showColorScheme,
                        showPhysicalProperties: isProteinAlignmentView,
                        fivePrimeThreePrimeHints,
                        scrollData,
                        hideBps: charWidth < 7,
                        sequence: alignmentData ? alignmentData.sequence : row.sequence,
                        rowStart: row.start,
                        rowEnd: row.end,
                        height: sequenceHeight,
                        showCutsites,
                        charWidth,
                        alignmentData,
                        ...annotationCommonProps,
                        children: [
                          showCutsites && Object.keys(cutsites).length > 0 && /* @__PURE__ */ jsx(
                            Cutsites,
                            {
                              sequenceLength,
                              annotationRanges: cutsites,
                              topStrand: true,
                              ...annotationCommonProps
                            }
                          ),
                          deletionLayerStrikeThrough
                        ]
                      }
                    ),
                    emptyText,
                    showReverseSequence && /* @__PURE__ */ jsxs(
                      Sequence,
                      {
                        scrollData,
                        sequenceLength,
                        fivePrimeThreePrimeHints,
                        isReverse: true,
                        cutsites,
                        showDnaColors,
                        hideBps: charWidth < 7,
                        length: reverseSequence.length,
                        showCutsites,
                        rowStart: row.start,
                        rowEnd: row.end,
                        sequence: reverseSequence,
                        height: sequenceHeight,
                        charWidth,
                        children: [
                          showCutsites && Object.keys(cutsites).length > 0 && /* @__PURE__ */ jsx(
                            Cutsites,
                            {
                              topStrand: false,
                              annotationRanges: cutsites,
                              ...annotationCommonProps
                            }
                          ),
                          deletionLayerStrikeThrough
                        ]
                      }
                    ),
                    showCutsites && showCutsitesInSequence && /* @__PURE__ */ jsx(
                      CutsiteSelectionLayers,
                      {
                        ...{
                          editorName,
                          cutsites,
                          annotationCommonProps,
                          showReverseSequence,
                          sequenceHeight,
                          alignmentData,
                          row
                        }
                      }
                    )
                  ]
                }
              ),
              drawAnnotations("primer", {
                sequence: fullSequence,
                annotationHeight: primerHeight,
                onlyReverse: true
              }),
              drawLabels("primer", false, {
                filterOpts: { onlyReverse: true }
              }),
              drawLabels("cutsite"),
              drawLabels("feature"),
              drawAnnotations("feature"),
              map(replacementLayers, function(replacementLayer) {
                if (!replacementLayer) return null;
                const atCaret = replacementLayer.caretPosition > -1;
                let normedCaretPos;
                if (atCaret) {
                  normedCaretPos = normalizePositionByRangeLength(
                    replacementLayer.caretPosition,
                    sequenceLength
                  );
                }
                const insertedBpsLayer = {
                  ...replacementLayer,
                  start: atCaret ? normedCaretPos : replacementLayer.start,
                  end: (atCaret ? normedCaretPos : replacementLayer.start) + replacementLayer.sequence.length
                };
                const { sequence: sequence2 } = insertedBpsLayer;
                const layerRangeOverlaps = getOverlapsOfPotentiallyCircularRanges(
                  insertedBpsLayer,
                  row,
                  sequenceLength
                );
                return layerRangeOverlaps.map(function(layer, index) {
                  const isStart = layer.start === insertedBpsLayer.start;
                  const seqInRow = getSequenceWithinRange(
                    {
                      start: layer.start - insertedBpsLayer.start,
                      end: layer.end - insertedBpsLayer.start
                    },
                    sequence2
                  );
                  const startOffset = layer.start - row.start;
                  const width2 = seqInRow.length * charWidth;
                  const height2 = sequenceHeight;
                  const bufferBottom = 4;
                  const bufferLeft = 2;
                  const arrowHeight = isStart ? 8 : 0;
                  return /* @__PURE__ */ jsx(
                    Sequence,
                    {
                      sequenceLength,
                      isReplacementLayer: true,
                      showDnaColors,
                      sequence: seqInRow,
                      startOffset,
                      height: height2,
                      length: seqInRow.length,
                      charWidth,
                      children: /* @__PURE__ */ jsx(
                        "svg",
                        {
                          style: {
                            left: startOffset * charWidth,
                            height: sequenceHeight,
                            position: "absolute",
                            userSelect: "none"
                          },
                          onClick: function(event) {
                            replacementLayerClicked({
                              annotation: replacementLayer,
                              event
                            });
                          },
                          onContextMenu: function(event) {
                            replacementLayerRightClicked({
                              annotation: replacementLayer,
                              event
                            });
                          },
                          className: "rowViewTextContainer clickable",
                          width: Math.max(0, Number(width2)),
                          height: Math.max(0, Number(height2)),
                          children: /* @__PURE__ */ jsx(
                            "polyline",
                            {
                              points: `${-bufferLeft},0 ${-bufferLeft},${-arrowHeight}, ${charWidth / 2},0 ${width2},0 ${width2},${height2 + bufferBottom} ${-bufferLeft},${height2 + bufferBottom} ${-bufferLeft},0`,
                              fill: "none",
                              stroke: "black",
                              strokeWidth: "2px"
                            }
                          )
                        }
                      )
                    },
                    index
                  );
                });
              }),
              drawAnnotations("primaryProteinSequence", {
                ...translationCommonProps,
                noPlural: true
              }),
              drawLabels("cutsite", isRowView),
              showAxis && /* @__PURE__ */ jsx(
                Axis,
                {
                  scrollData,
                  tickSpacing,
                  showAxisNumbers,
                  annotationHeight: axisHeight,
                  marginTop: axisMarginTop,
                  ...annotationCommonProps,
                  isLinearView
                }
              ),
              caretPosition > -1 && /* @__PURE__ */ jsx(
                Caret,
                {
                  caretPosition,
                  isProtein,
                  showAminoAcidUnitAsCodon,
                  ...{ ...annotationCommonProps, ...{ getGaps: void 0 } },
                  row: alignmentData ? { start: 0, end: alignmentData.sequence.length - 1 } : row
                }
              ),
              /* @__PURE__ */ jsx(
                FeatureDragGuideline,
                {
                  row,
                  alignmentData,
                  isProtein,
                  showAminoAcidUnitAsCodon,
                  annotationCommonProps
                }
              )
            ]
          }
        ),
        rowBottomComp && rowBottomComp
      ]
    }
  );
}
const FeatureDragGuideline = view(function FeatureDragGuideline2({
  row,
  alignmentData,
  isProtein,
  showAminoAcidUnitAsCodon,
  annotationCommonProps
}) {
  const { dragCaretPosition } = featureDragStore;
  if (dragCaretPosition === null || dragCaretPosition === void 0) return null;
  return /* @__PURE__ */ jsx(
    Caret,
    {
      caretPosition: dragCaretPosition,
      isProtein,
      showAminoAcidUnitAsCodon,
      ...{ ...annotationCommonProps, ...{ getGaps: void 0 } },
      className: "veFeatureDragGuideline",
      row: alignmentData ? { start: 0, end: alignmentData.sequence.length - 1 } : row
    }
  );
});
function getGapMap(sequence) {
  const gapMap = [0];
  sequence.split("").forEach((char) => {
    if (char === "-") {
      gapMap[Math.max(0, gapMap.length - 1)] = (gapMap[Math.max(0, gapMap.length - 1)] || 0) + 1;
    } else {
      gapMap.push(gapMap[gapMap.length - 1] || 0);
    }
  });
  return gapMap;
}
function getGapsDefault() {
  return {
    gapsBefore: 0,
    gapsInside: 0
  };
}
